import type { Receipt, SignedStep } from "../api/types.js";
import { clock } from "../lib/format.js";
import { shortDate } from "./schedule-words.js";
import type { IconId } from "../lib/icon.js";

/**
 * A card's end (UX spec v3 §C.10, copy §C.13): after Signed, Expired, Cancelled or
 * Not sent there is no sign button — not greyed, not disabled, gone — and no Edit and
 * no leaves line. Its place is one line: what happened and when, the consequence, and
 * at most one small action. The time is the bank's (`signed_at`, the payload's
 * `expires_at`, a cancel's own time), never the phone's clock; with no bank time the
 * line says what happened without one.
 */
export type EndKind = "signed" | "expired" | "cancelled" | "not-sent" | "changed";

export interface EndLine {
  readonly status: string;
  readonly icon: IconId;
  readonly ok: boolean;
  readonly headline: string;
  readonly consequence: string | undefined;
  /** A refusal's rule id, printed whole after the consequence (the reason is never "UNKNOWN"). */
  readonly rule?: string;
  readonly action: { readonly label: string; readonly aria?: string } | undefined;
}

export interface Done {
  readonly ran: number;
  /** DCTA-143 (§F.2 18f): steps that wait (held) — with `ran` 0 and `held` 0 nothing of the signature went or waits. */
  readonly held: number;
  readonly of: number;
}

/** "Nothing left your accounts." — true only of a whole signature nothing of which went or waits (§F.2 18d/18f). */
export const NOTHING_LEFT = "Nothing left your accounts.";

/** §F.2 18f (DCTA-143): signed, and nothing of it went through or waits — the one test every surface uses. */
export function nothingWent(done: Done): boolean {
  return done.of > 0 && done.ran === 0 && done.held === 0;
}

/** How many of the payload's steps the bank reports as executed, and as held (success-model's rule). */
export function doneOf(steps: readonly SignedStep[], receipt: Receipt): Done {
  const states = new Map<string, string>();
  for (const r of receipt.steps) {
    states.set(r.index, r.state);
    if (r.step_id !== undefined) states.set(r.step_id, r.state);
  }
  const stateOf = (s: SignedStep): string | undefined => states.get(s.step_id) ?? states.get(s.index);
  const ran = steps.filter((s) => stateOf(s) === "executed").length;
  const held = steps.filter((s) => stateOf(s) === "held").length;
  return { ran, held, of: steps.length };
}

/**
 * `adds`: the card adds a payee (17's Check and sign, DCTA-124) — it moves no money, so its ends say what was
 * not added, never what did not leave the accounts.
 */
export function endLine(kind: EndKind, at: string | undefined, done?: Done, adds = false): EndLine {
  const when = (verb: string): string => (at === undefined ? verb : `${verb} at ${clock(at)}`);
  const nothing = adds ? "Nothing was added." : NOTHING_LEFT;
  switch (kind) {
    case "signed":
      // DCTA-143 × §F.2 18f (ask F19): signed, and nothing of it went through or waits (every step refused or stopped
      // when it ran) — the end says "Not sent", without the positive colour, and keeps its time: it WAS signed.
      if (done !== undefined && nothingWent(done)) {
        return { status: "Not sent", icon: "i-alert", ok: false, headline: when("Signed"), consequence: nothing, action: { label: "Receipt" } };
      }
      return {
        status: "Signed",
        icon: "i-check",
        ok: true,
        headline: when("Signed"),
        consequence: done === undefined ? undefined : `${String(done.ran)} of ${String(done.of)} done.`,
        action: { label: "Receipt" },
      };
    case "expired":
      return {
        status: "Expired",
        icon: "i-clock",
        ok: false,
        headline: when("Expired"),
        consequence: "Nothing was signed.",
        action: { label: "Ask again", aria: "Ask again with the same words" },
      };
    case "cancelled":
      return { status: "Cancelled", icon: "i-x", ok: false, headline: when("Cancelled"), consequence: nothing, action: undefined };
    case "changed":
      // DCTA-101 run 2: the gateway honoured the signed projection (POL-BAL-PROJECTED) and ran nothing.
      return {
        status: "Not sent",
        icon: "i-alert",
        ok: false,
        headline: "Your balance changed",
        consequence: "Nothing left your accounts. Update it to sign with your balance as it is now.",
        rule: "POL-BAL-PROJECTED",
        action: { label: "Update", aria: "Update: the same payment, with your balance as it is now" },
      };
    case "not-sent":
      return {
        status: adds ? "Not added" : "Not sent",
        icon: "i-alert",
        ok: false,
        headline: adds ? "Not added" : "Not sent",
        consequence: `Couldn't reach the bank. ${nothing}`,
        action: { label: "Try again" },
      };
  }
}

/**
 * DCTA-209 (F56, 09-scheduled.md row 27m): a signature that created a schedule — nothing moved, so the end says
 * "Scheduled" (the positive pill), the bank's time of it, and the first payment's date (the signed `first_on`, as the card
 * showed it); its one action opens the schedule. Never "Sent", never "1 of 1 done".
 */
export function scheduledEnd(at: string | undefined, first: string | undefined): EndLine {
  return {
    status: "Scheduled",
    icon: "i-check",
    ok: true,
    headline: at === undefined ? "Scheduled" : `Scheduled at ${clock(at)}`,
    consequence: first === undefined ? undefined : `First payment ${shortDate(first)}`,
    action: { label: "View", aria: "View this scheduled payment" },
  };
}

/**
 * A signature whose answer never came back (the network dropped it, or a proxy said
 * 5xx): what the bank's OWN record of the bundle (`GET /v1/bundles/{id}`) says
 * happened. Only "still waiting for a signature" lets the card say that nothing left
 * the accounts; anything that ran is recovered from the receipt; a bundle mid-flight
 * (`signing`) or in a state this build does not know is not guessed at.
 */
export type LostConfirm = "not-sent" | "expired" | "recover" | "cancelled" | "unknown";

const RECOVER: ReadonlySet<string> = new Set(["signed", "awaiting_step_up", "held", "executed", "partially_executed"]);

export function afterLostConfirm(state: string, expired: boolean): LostConfirm {
  if (state === "awaiting_signature") return expired ? "expired" : "not-sent";
  if (RECOVER.has(state)) return "recover";
  if (state === "cancelled_by_customer") return "cancelled";
  return "unknown";
}

/**
 * QA-04: a card that was in play when the page reloaded, drawn again from the bank's OWN record of it (never from what the
 * page kept). Still waiting for a signature and in its time: the card to sign, live. Anything else ends it, exactly as a
 * lost signature's record does; in flight or unknown, the past stays as it was drawn — nothing is guessed.
 */
export type AfterReload = "live" | "expired" | "recover" | "cancelled" | "keep";

export function afterReload(state: string, expired: boolean): AfterReload {
  if (state === "awaiting_signature" && !expired) return "live";
  const lost = afterLostConfirm(state, expired);
  return lost === "unknown" || lost === "not-sent" ? "keep" : lost;
}

/**
 * DCTA-101 run 2 (radar Card 2; Claude's ruling: the strict rule stays). A /2 payload signs the
 * available balance each debited account starts from; the gateway runs nothing more of the bundle
 * when the live balance is not that one, and says so in each step's result: POLICY_STATE_CHANGED
 * under POL-BAL-PROJECTED. Only when NOTHING ran or waits is that the card's end ("changed") —
 * a step that already ran (a resume after a step-up) makes it a receipt, which says what ran.
 */
export function balanceChanged(receipt: Receipt): boolean {
  if (receipt.steps.length === 0) return false;
  if (receipt.steps.some((s) => s.state !== "not_executed")) return false;
  return receipt.steps.some((s) => s.reason_code === "POLICY_STATE_CHANGED" && (s.rule_ids ?? []).includes("POL-BAL-PROJECTED"));
}
